import axiosInstance from "@/api/axiosInstance";
import { FreeSubscriptionResponseDTO } from "@/common/model/dto/response/FreeSubscriptionResponse.dto";
import { FreeSubscription } from "@/common/model/FreeSubscription";
import { ENDPOINTS } from "@/constants/api";
import { requestHandler } from "@/helpers/requestHandler";
import { FreeSubscriptionMapper } from "@/mappers/FreeSubscriptionMapper";
import type { StripeSubscriptionRow } from "@/components/user/tabs/subscriptions/productResolver";

export const FreeSubscriptionService = {
  async fetchAll(): Promise<FreeSubscription[]> {
    return requestHandler<FreeSubscription[]>(async () => {
      const response = await axiosInstance.get(ENDPOINTS.FREESUBSCRIPTIONS.ALL);
      return response.data.map(
        (freeSubscription: FreeSubscriptionResponseDTO) =>
          FreeSubscriptionMapper.fromResponse(freeSubscription)
      );
    }, "FreeSubscriptionService -> fetchAll");
  },

  /**
   * Stripe-billed subscriptions for ONE user.
   *
   * Stripe rows are excluded server-side from /free-subscription/all (they are not
   * admin-grantable), so without this call a Stripe-paying rider shows no subscription at
   * all in the PRO Details tab. Returns the raw rows; mapping lives in productResolver.
   */
  async fetchStripeForUser(userId: number): Promise<StripeSubscriptionRow[]> {
    return requestHandler<StripeSubscriptionRow[]>(async () => {
      const response = await axiosInstance.get(ENDPOINTS.FREESUBSCRIPTIONS.STRIPE, {
        params: { userId },
      });
      return Array.isArray(response.data) ? response.data : [];
    }, "FreeSubscriptionService -> fetchStripeForUser");
  },

  async create(
    subscription: Partial<FreeSubscription>
  ): Promise<FreeSubscription> {
    return requestHandler<FreeSubscription>(async () => {
      const response = await axiosInstance.post(
        ENDPOINTS.FREESUBSCRIPTIONS.DEFAULT,
        FreeSubscriptionMapper.toRequest(subscription)
      );
      if (response.data.error) {
        throw new Error(response.data.message);
      }
      return FreeSubscriptionMapper.fromResponse(response.data);
    }, "FreeSubscriptionService -> create");
  },

  async update(
    id: string,
    subscription: Partial<FreeSubscription>
  ): Promise<FreeSubscription> {
    const dto = FreeSubscriptionMapper.toRequest(subscription);
    return requestHandler<FreeSubscription>(async () => {
      const response = await axiosInstance.put(
        `${ENDPOINTS.FREESUBSCRIPTIONS.DEFAULT}/${id}`,
        dto
      );
      if (response.data.error) {
        throw new Error(response.data.message);
      }
      return FreeSubscriptionMapper.fromResponse(response.data);
    }, "FreeSubscriptionService -> update");
  },

  async endNow(
    subscription: Partial<FreeSubscription>
  ): Promise<FreeSubscription> {
    return requestHandler<FreeSubscription>(async () => {
      const response = await axiosInstance.post(
        ENDPOINTS.FREESUBSCRIPTIONS.END,
        FreeSubscriptionMapper.toRequest(subscription)
      );
      return FreeSubscriptionMapper.fromResponse(response.data);
    }, "FreeSubscriptionService -> create");
  },

  async delete(id: string): Promise<void> {
    return requestHandler<void>(async () => {
      await axiosInstance.delete(
        `${ENDPOINTS.FREESUBSCRIPTIONS.DEFAULT}/${id}`
      );
    }, "FreeSubscriptionService -> delete");
  },
};
